import { useEffect, useState } from "react";
import { Pencil, Plus, Save, Trash2, Clock } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  createProject,
  deleteProject,
  getTimesheetPolicy,
  listProjects,
  updateProject,
  upsertTimesheetPolicy,
} from "@/api/time";
import type { Project, TimesheetPolicy } from "@/types/time";

interface ProjectForm {
  code: string;
  name: string;
  client: string;
  description: string;
  is_billable: boolean;
  hourly_rate: string;
  currency: string;
  is_active: boolean;
}

const EMPTY_PROJECT: ProjectForm = {
  code: "",
  name: "",
  client: "",
  description: "",
  is_billable: false,
  hourly_rate: "",
  currency: "USD",
  is_active: true,
};

export function ProjectsSettings() {
  const { addToast } = useToast();

  const [projects, setProjects] = useState<Project[]>([]);
  const [policy, setPolicy] = useState<TimesheetPolicy | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState<ProjectForm>(EMPTY_PROJECT);

  const load = async () => {
    try {
      const [p, pol] = await Promise.all([
        listProjects(true),
        getTimesheetPolicy(),
      ]);
      setProjects(p);
      setPolicy(pol);
    } catch {
      addToast("error", "Failed to load projects");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_PROJECT);
    setOpen(true);
  };

  const openEdit = (p: Project) => {
    setEditing(p);
    setForm({
      code: p.code,
      name: p.name,
      client: p.client ?? "",
      description: p.description ?? "",
      is_billable: p.is_billable,
      hourly_rate: p.hourly_rate != null ? String(p.hourly_rate) : "",
      currency: p.currency,
      is_active: p.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload: Partial<Project> = {
        code: form.code.trim(),
        name: form.name.trim(),
        client: form.client || null,
        description: form.description || null,
        is_billable: form.is_billable,
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
        currency: form.currency,
        is_active: form.is_active,
      };
      if (editing) {
        await updateProject(editing.id, payload);
      } else {
        await createProject(payload);
      }
      setOpen(false);
      addToast("success", "Project saved");
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteProject(id);
      addToast("success", "Project deactivated");
      await load();
    } catch {
      addToast("error", "Failed to delete");
    }
  };

  const savePolicy = async () => {
    if (!policy) return;
    try {
      const updated = await upsertTimesheetPolicy({
        weekly_overtime_threshold: Number(policy.weekly_overtime_threshold),
        daily_overtime_threshold: Number(policy.daily_overtime_threshold),
        require_project_on_entry: policy.require_project_on_entry,
        auto_submit_on_friday: policy.auto_submit_on_friday,
        default_hourly_rate: policy.default_hourly_rate ?? null,
      });
      setPolicy(updated);
      addToast("success", "Policy saved");
    } catch {
      addToast("error", "Failed to save policy");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Timesheet policy</CardTitle>
          <Button size="sm" onClick={savePolicy} disabled={!policy}>
            <Save className="h-4 w-4" />
            Save
          </Button>
        </CardHeader>
        {policy ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Weekly OT threshold (hrs)"
                type="number"
                step="0.5"
                value={policy.weekly_overtime_threshold}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    weekly_overtime_threshold: Number(e.target.value),
                  })
                }
              />
              <Input
                label="Daily OT threshold (hrs)"
                type="number"
                step="0.5"
                value={policy.daily_overtime_threshold}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    daily_overtime_threshold: Number(e.target.value),
                  })
                }
              />
              <Input
                label="Default hourly rate (USD)"
                type="number"
                step="0.01"
                value={policy.default_hourly_rate ?? ""}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    default_hourly_rate: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="rounded border-border text-accent"
                checked={policy.require_project_on_entry}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    require_project_on_entry: e.target.checked,
                  })
                }
              />
              Require project selection on every time entry
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="rounded border-border text-accent"
                checked={policy.auto_submit_on_friday}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    auto_submit_on_friday: e.target.checked,
                  })
                }
              />
              Auto-submit timesheets Friday at week-end
            </label>
          </div>
        ) : (
          <p className="text-sm text-text-muted">Loading…</p>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Projects</CardTitle>
          <Button size="sm" onClick={openNew}>
            <Plus className="h-4 w-4" />
            Add project
          </Button>
        </CardHeader>

        {projects.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-muted">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No projects yet. Add one so employees can track time against it.
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {projects.map((p) => (
              <div
                key={p.id}
                className="py-3 flex items-center gap-3 justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-text-muted">
                      {p.code}
                    </span>
                    <p className="font-medium text-text-primary truncate">
                      {p.name}
                    </p>
                    {p.is_billable && <Badge variant="info">Billable</Badge>}
                    {!p.is_active && <Badge variant="default">Inactive</Badge>}
                  </div>
                  <p className="text-xs text-text-muted mt-0.5 truncate">
                    {[
                      p.client,
                      p.hourly_rate != null
                        ? `${p.currency} ${p.hourly_rate}/hr`
                        : null,
                      p.description,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(p)}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {p.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(p.id)}
                    title="Deactivate"
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit project" : "Add project"}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="PROJ-001"
            />
            <Input
              label="Client"
              value={form.client}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
            />
          </div>
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Hourly rate"
              type="number"
              step="0.01"
              value={form.hourly_rate}
              onChange={(e) =>
                setForm({ ...form, hourly_rate: e.target.value })
              }
            />
            <Input
              label="Currency"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              maxLength={3}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-border text-accent"
              checked={form.is_billable}
              onChange={(e) =>
                setForm({ ...form, is_billable: e.target.checked })
              }
            />
            Billable (rolls up to invoicing)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-border text-accent"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
            Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save}>
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
